export const MANUAL_KEY = 'zens.localization.manual.v1';
export const SESSION_KEY = 'zens.localization.attempt.v1';
const TAIWAN_ORIGIN = 'https://zenstech.com.tw';
const TAIWAN_PATHS = new Set(['/', '/pages/about', '/pages/company_itroduction', '/collections/pjsk', '/collections/p5r']);
const CAMPAIGN_KEYS = new Set(['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id', 'gclid']);
const PAGE_TYPES = new Set(['index', 'page', 'product', 'collection', 'list-collections', 'blog', 'article', 'search']);
const CRAWLER = /bot|crawl|spider|slurp|bingpreview|facebookexternalhit|facebot|headless|lighthouse|pagespeed|prerender|preview|validator/i;

const noChange = reason => ({ type: 'none', reason });
const genericEnglishPath = pathname => /^\/en(?:\/|$)/i.test(pathname);

export function explicitLocalePath(pathname, languageCodes = []) {
  // Generic /en selects a language only; a native language redirect may precede currency selection.
  if (genericEnglishPath(pathname)) return false;
  const first = pathname.split('/')[1].toLowerCase();
  return languageCodes.some(code => {
    const language = String(code).toLowerCase().split('-')[0];
    return first === language || first.startsWith(`${language}-`);
  });
}

export function storefrontPath(pathname) {
  const path = pathname.replace(/^\/(?:en(?:-(?:hk|ca|us))?|zh-(?:hk|tw))(?=\/|$)/i, '') || '/';
  return path === '/' ? path : path.replace(/\/$/, '');
}

export function environmentBlock(config, url, environment = {}, { taiwanDetection = false } = {}) {
  if (!['zenstech.com', 'www.zenstech.com'].includes(url.hostname)) return 'other_host';
  if (config.themeRole !== 'main' || environment.designMode || environment.framed) return 'preview';
  if (['preview_theme_id', 'preview_key', '_fd', '_sc', 'pb'].some(key => url.searchParams.has(key))) return 'preview';
  if (CRAWLER.test(environment.userAgent || '') || environment.webdriver || environment.hidden) return 'crawler_or_background';
  let path;
  try { path = storefrontPath(decodeURIComponent(url.pathname)); } catch { return 'protected_page'; }
  if (/^\/(?:admin|account|checkout|checkouts|orders|authentication|challenge|password)(?:\/|$)/i.test(path)) return 'protected_page';
  if (!PAGE_TYPES.has(config.pageType) && !(taiwanDetection && config.pageType === 'cart' && path === '/cart')) return 'protected_page';
  if (!taiwanDetection && explicitLocalePath(url.pathname, config.languageCodes)) return 'explicit_locale';
  return null;
}

export function confidentCountry(data, country) {
  return Array.isArray(data?.suggestions) && data.suggestions.some(suggestion =>
    suggestion?.confidence === 1 && suggestion.parts?.country?.handle === country && suggestion.parts.country.confidence === 1
  );
}

export function planLocalization(config, data, url) {
  const detected = data?.detected_values?.country?.handle;
  if (typeof detected !== 'string' || !/^[A-Z]{2}$/.test(detected)) return noChange('no_detected_country');
  if (detected === 'TW') {
    const path = storefrontPath(url.pathname);
    const mapped = TAIWAN_PATHS.has(path);
    const destination = new URL(mapped ? path : '/', TAIWAN_ORIGIN);
    for (const [key, value] of url.searchParams) {
      if (CAMPAIGN_KEYS.has(key)) destination.searchParams.append(key, value);
    }
    return {
      type: 'redirect',
      country: 'TW',
      reason: mapped ? 'taiwan_mapped' : 'taiwan_root_fallback',
      destination: destination.href
    };
  }
  const chinese = detected === 'HK' || detected === 'MO';
  const preserveHongKongContext = detected === 'CN' || detected === 'JP';
  const currency = chinese || preserveHongKongContext ? 'HKD' : detected === 'CA' ? 'CAD' : 'USD';
  let language = chinese ? 'zh-TW' : 'en';
  const countries = Array.isArray(config.countries) ? config.countries : [];
  let target = countries.find(country => country.code === detected);
  let displayFallback = false;
  if (preserveHongKongContext) {
    target = countries.find(country => country.code === 'HK');
    language = target?.languages?.includes(config.language) ? config.language : 'zh-TW';
    displayFallback = true;
  } else if (currency === 'USD' && (!target || target.currency !== 'USD')) {
    target = countries.find(country => country.code === 'US');
    displayFallback = true;
  }
  // Keep the requested English language while still allowing the first-visit country/currency default.
  if (genericEnglishPath(url.pathname)) language = 'en';
  if (!target) return noChange('country_not_enabled');
  if (target.currency !== currency) return noChange('market_currency_not_configured');
  if (!Array.isArray(target.languages) || !target.languages.includes(language)) return noChange('language_not_available');
  if (target.code !== config.country && !displayFallback && !confidentCountry(data, detected)) return noChange('country_confidence_missing');
  if (target.code === config.country && config.language === language) return noChange('already_matches');
  return {
    type: 'localize',
    reason: displayFallback ? 'first_visit_display_fallback' : 'first_visit_default',
    country: target.code,
    language,
    returnTo: `${url.pathname}${url.search}${url.hash}`
  };
}

export async function applyPlan(plan, adapters) {
  if (plan.type === 'none') return plan.reason;
  if (plan.type === 'redirect' && plan.country === 'TW') {
    if (!adapters.canRedirectTaiwan()) return 'taiwan_navigation_changed';
    adapters.redirect(plan.destination);
    return 'taiwan_redirected';
  }
  if (!adapters.canContinue()) return 'manual_or_interaction';
  if (plan.type === 'localize') {
    adapters.localize(plan);
    return 'localization_submitted';
  }
  let cart;
  try { cart = await adapters.fetchCart(); } catch { return 'cart_check_failed'; }
  if (!Number.isInteger(cart?.item_count) || cart.item_count !== 0) return 'cross_store_cart';
  if (!adapters.canContinue()) return 'manual_or_interaction';
  if (plan.type === 'redirect') {
    adapters.redirect(plan.destination);
    return 'taiwan_redirected';
  }
  adapters.showChoice(plan.destination);
  return 'taiwan_choice_shown';
}

export function createPreferenceState(localStorage, sessionStorage) {
  let manual = false;
  let usable = true;
  try { manual = localStorage.getItem(MANUAL_KEY) !== null; } catch { usable = false; }
  return {
    hasManual() {
      if (manual || !usable) return true;
      try { return localStorage.getItem(MANUAL_KEY) !== null; } catch { return true; }
    },
    remember() {
      manual = true;
      try { localStorage.setItem(MANUAL_KEY, '1'); } catch { usable = false; }
    },
    begin() {
      if (!usable || this.hasManual()) return false;
      try {
        if (sessionStorage.getItem(SESSION_KEY) !== null) return false;
        sessionStorage.setItem(SESSION_KEY, '1');
        if (sessionStorage.getItem(SESSION_KEY) !== '1') return false;
        localStorage.setItem('zens.localization.storage_check', '1');
        localStorage.removeItem('zens.localization.storage_check');
        return true;
      } catch { return false; }
    }
  };
}

export function isLocalizationForm(form, origin) {
  if (!form || form.id === 'zens-locale-default-form') return false;
  try {
    const action = new URL(form.action, origin);
    return action.origin === origin && /\/(?:[a-z]{2,3}(?:-[a-z0-9]+)*\/)?localization\/?$/i.test(action.pathname);
  } catch { return false; }
}

function setField(form, name, value, document) {
  let field = form.querySelector(`[name="${name}"]`);
  if (!field) {
    field = document.createElement('input');
    field.type = 'hidden';
    field.name = name;
    form.append(field);
  }
  field.value = value;
}

export function synchronizeManualLanguage(event, form, config, document) {
  if (event.type !== 'change') return;
  const control = event.target?.name ? event.target : event.target?.closest?.('.localization__selector')?.firstElementChild;
  const name = control?.name;
  const value = event.detail?.selectedValue ?? event.target?.value;
  if (typeof value !== 'string') return;
  let language;
  if (name === 'country_code') {
    const country = config.countries?.find(item => item.code === value);
    if (['USD', 'CAD'].includes(country?.currency) && country?.languages?.includes('en')) language = 'en';
  } else if (name === 'language_code' || name === 'locale_code') {
    if (config.languageCodes?.includes(value)) language = value;
  }
  if (!language) return;
  setField(form, 'language_code', language, document);
  for (const field of form.querySelectorAll('[name="locale_code"]')) field.value = language;
}

function waitUntilVisible(document) {
  if (!document.hidden) return Promise.resolve();
  return new Promise(resolve => {
    const resume = () => {
      if (document.hidden) return;
      document.removeEventListener('visibilitychange', resume);
      resolve();
    };
    document.addEventListener('visibilitychange', resume);
  });
}

export async function startLocalization(window, document) {
  const holder = document.getElementById('zens-locale-default');
  const configNode = holder?.querySelector('[data-zens-locale-config]');
  const form = document.getElementById('zens-locale-default-form');
  const choice = document.getElementById('zens-taiwan-store-choice');
  if (!holder || !configNode || !form || !choice || holder.dataset.started) return;
  let config;
  try { config = JSON.parse(configNode.textContent); } catch { return; }
  const url = new URL(window.location.href);
  const environment = {
    designMode: window.Shopify?.designMode,
    framed: window.self !== window.top,
    userAgent: window.navigator.userAgent,
    webdriver: window.navigator.webdriver,
    hidden: document.hidden
  };
  const blocked = environmentBlock(config, url, { ...environment, hidden: false }, { taiwanDetection: true });
  if (blocked === 'other_host' || environment.designMode || environment.framed) {
    holder.dataset.status = blocked;
    return;
  }
  if (!blocked && document.hidden) {
    if (holder.dataset.visibilityPending) return;
    holder.dataset.visibilityPending = '1';
    holder.dataset.status = 'waiting_for_visibility';
    await waitUntilVisible(document);
    delete holder.dataset.visibilityPending;
    return startLocalization(window, document);
  }
  holder.dataset.started = '1';
  let preferences;
  try { preferences = createPreferenceState(window.localStorage, window.sessionStorage); }
  catch { preferences = createPreferenceState(null, null); }
  let interacted = false;
  const rememberLocalization = event => {
    const changedForm = event.target?.closest?.('form');
    if (isLocalizationForm(changedForm, url.origin)) {
      synchronizeManualLanguage(event, changedForm, config, document);
      if (event.type === 'change') {
        const currentUrl = new URL(window.location.href);
        setField(changedForm, 'return_to', `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`, document);
      }
      preferences.remember();
    }
  };
  document.addEventListener('change', rememberLocalization, true);
  document.addEventListener('submit', rememberLocalization, true);
  document.addEventListener('pointerdown', () => { interacted = true; }, { once: true, capture: true });
  document.addEventListener('keydown', () => { interacted = true; }, { once: true, capture: true });
  choice.querySelector('[data-zens-locale-dismiss]').addEventListener('click', () => {
    preferences.remember();
    choice.hidden = true;
  });
  choice.querySelector('[data-zens-taiwan-link]').addEventListener('click', () => preferences.remember());
  if (blocked) {
    holder.dataset.status = blocked;
    return;
  }
  if (explicitLocalePath(url.pathname, config.languageCodes)) preferences.remember();
  const root = new URL(config.root.endsWith('/') ? config.root : `${config.root}/`, url.origin);
  if (root.origin !== url.origin || !root.pathname.startsWith('/')) return;
  const fetchJSON = async path => {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 1800);
    try {
      const response = await window.fetch(new URL(path, root), {
        credentials: 'same-origin', redirect: 'error', signal: controller.signal,
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error('Localization request failed');
      return await response.json();
    } finally { window.clearTimeout(timeout); }
  };
  let data;
  try { data = await fetchJSON('browsing_context_suggestions.json?country[enabled]=true&language[enabled]=false'); }
  catch {
    holder.dataset.status = 'suggestion_request_failed';
    return;
  }
  const plan = planLocalization(config, data, url);
  if (plan.country === 'TW' && document.hidden) {
    holder.dataset.status = 'waiting_for_visibility';
    await waitUntilVisible(document);
  }
  if (plan.country !== 'TW') {
    const nonTaiwanBlock = environmentBlock(config, url, environment);
    if (nonTaiwanBlock === 'explicit_locale') preferences.remember();
    if (nonTaiwanBlock) {
      holder.dataset.status = nonTaiwanBlock;
      return;
    }
    if (!preferences.begin()) {
      holder.dataset.status = 'manual_or_session_guard';
      return;
    }
  }
  const status = await applyPlan(plan, {
    canRedirectTaiwan: () => !document.hidden && window.location.href === url.href,
    canContinue: () => !interacted && !preferences.hasManual() && !document.hidden && window.location.href === url.href,
    fetchCart: () => fetchJSON('cart.js'),
    localize: action => {
      setField(form, 'country_code', action.country, document);
      setField(form, 'language_code', action.language, document);
      setField(form, 'return_to', action.returnTo, document);
      form.submit();
    },
    redirect: destination => window.location.replace(destination),
    showChoice: destination => {
      choice.querySelector('[data-zens-taiwan-link]').href = destination;
      choice.hidden = false;
    }
  });
  holder.dataset.status = status;
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  startLocalization(window, document).catch(() => {});
}
